import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useSubscription } from "@/subscription/SubscriptionContext";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRO_FEATURES = [
  "AI Beat Lab — 200 Fast + 20 Master beats/mo",
  "Studio-grade, sellable Master beats",
  "Unlimited AI Song Starter & saved songs",
  "Vocal remover + stem splitter",
  "Export to WAV · no watermark",
];

export default function UpgradeModal() {
  const { showUpgrade, setShowUpgrade, upgradeReason } = useSubscription();
  const { user, setShowAuth, requireAuth } = useAuth();
  const [cycle, setCycle] = useState("yearly");
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (!requireAuth()) {
      setShowUpgrade(false);
      return;
    }
    setBusy(true);
    try {
      const lookup_key = cycle === "yearly" ? "pro_yearly" : "pro_monthly";
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 401) setShowAuth(true);
      else toast.error("Could not start checkout, please try again");
      setBusy(false);
    }
  };

  return (
    <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
      <DialogContent className="bg-[#0d1416] border-[#00e5ff]/40 text-white max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-widest flex items-center gap-2 text-[#00e5ff]">
            <Crown size={18} /> BEATMXR PRO
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-xs">
            {upgradeReason || "Unlock the full studio. 7-day free trial, cancel anytime."}
          </DialogDescription>
        </DialogHeader>

        {/* billing cycle toggle */}
        <div className="flex items-center gap-2 mt-1 p-1 rounded-full bg-[#050505] border border-[#262626]">
          <button
            data-testid="cycle-monthly-btn"
            onClick={() => setCycle("monthly")}
            className="flex-1 py-2 rounded-full text-xs font-bold transition-colors"
            style={cycle === "monthly" ? { background: "#00e5ff", color: "#000" } : { color: "#a3a3a3" }}
          >
            $9.99 / month
          </button>
          <button
            data-testid="cycle-yearly-btn"
            onClick={() => setCycle("yearly")}
            className="flex-1 py-2 rounded-full text-xs font-bold transition-colors relative"
            style={cycle === "yearly" ? { background: "#00e5ff", color: "#000" } : { color: "#a3a3a3" }}
          >
            $99.99 / year
            <span className="absolute -top-2 -right-1 px-1.5 py-0.5 rounded-full bg-[#ff007f] text-white text-[9px] font-mono-data">
              2 MO FREE
            </span>
          </button>
        </div>

        <ul className="mt-4 space-y-2.5">
          {PRO_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-neutral-200">
              <Check size={16} className="text-[#00e5ff] mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <button
          data-testid="upgrade-checkout-btn"
          onClick={checkout}
          disabled={busy}
          className="mt-5 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          style={{ background: "#00e5ff", boxShadow: "0 0 22px rgba(0,229,255,0.5)" }}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {busy ? "Redirecting to checkout…" : "Start 7-Day Free Trial"}
        </button>
        <p className="text-[11px] text-neutral-600 text-center mt-1">
          {user?.id ? "Secure checkout via Stripe. Cancel anytime." : "You'll sign in first, then start your trial."}
        </p>
      </DialogContent>
    </Dialog>
  );
}
